/**
 * Le brouillon du composeur : ce que le parent compose, et les transitions qui le tiennent cohérent.
 *
 * Réducteur PUR, dans son propre module pour être joué transition par transition — c'est là que
 * vivent les règles que le legacy avait éparpillées entre `onChangeHoursPerWeek`,
 * `onChangeSubjects`, `getUpdateAllocationHoursFn` et trois `useEffect` d'invariant
 * (`ActivationComposerScreen.js:523-582`, `HourAllocationStep.js:8-92`).
 *
 * Les deux invariants sont tenus ICI plutôt que par un effet : une transition qui laisse l'état
 * incohérent le temps d'un rendu, c'est un sélecteur qui affiche « 2h » sans rien de sélectionné,
 * et une confirmation qui part sur un volume que le serveur refuse.
 *
 * 1. **Le palier vendu sans engagement s'impose** — quel que soit le chemin qui en a fait sortir
 *    (offre recommandée arrivée sans engagement, changement de formule, bascule de l'interrupteur).
 * 2. **Une classe refermée retaille la sélection** — passer de 3ème à 2nde referme le droit
 *    « plus de matières que d'heures » immédiatement, pas au prochain chargement.
 * 3. **Une formule qui ferme le dimanche retire ses créneaux** — en cours partagés, la colonne
 *    disparaît de la grille : la case restée cochée ne pourrait plus être décochée, et le submit
 *    la posterait (`ActivationComposerScreen.js`, l'effet sur `offerType`).
 */
import {
    allowsMoreSubjectsThanHours,
    defaultSubjectHoursAllocation,
    type ActivationDraft,
} from "../../../../adapters/parent/Activation/activationAdapter"
import { keepSelectableGridSlots } from "../../../../adapters/parent/shared/activationDisplay"

export type ActivationDraftState = {
    draft: ActivationDraft
    /**
     * Le palier UNIQUE vendu sans engagement, par formule, en heures par semaine. Règle du back
     * (`Subscription::SELF_SERVE_NO_COMMITMENT_HOURS`) : elle borne le brouillon, elle n'est
     * jamais postée. `null` pour une formule sans palier imposé — on ne restreint alors rien.
     */
    soldNoCommitmentHours: Record<string, number | null>
}

export type ActivationDraftAction =
    /** (Ré)amorçage depuis le contexte — l'offre recommandée n'arrive parfois qu'après le 1ᵉʳ rendu. */
    | { type: "SYNC"; draft: ActivationDraft; soldNoCommitmentHours: Record<string, number | null> }
    | { type: "SET_CLASS"; value: string }
    | { type: "SET_HOURS"; value: number }
    | { type: "SET_SUBJECTS"; value: (string | number)[] }
    | { type: "SET_ALLOCATION"; subjectId: string | number; hours: number }
    | { type: "SET_SLOTS"; value: string[] }
    | { type: "SET_RESUME_DATE"; value: string }
    | { type: "SET_OFFER"; value: string }
    | { type: "SET_COMMITMENT"; value: boolean }

export const EMPTY_ACTIVATION_DRAFT: ActivationDraft = {
    offerType: "essential",
    hoursPerWeek: 2,
    withCommitment: true,
    slots: [],
    pickedClass: null,
    subjectIds: [],
    hoursAllocation: {},
    pickedResumeDate: null,
}

export const initialActivationDraftState: ActivationDraftState = {
    draft: EMPTY_ACTIVATION_DRAFT,
    soldNoCommitmentHours: {},
}

/**
 * Volume retenu : la sélection de matières suit, et la répartition se ré-amorce.
 *
 * Port de `onChangeHoursPerWeek` (`ActivationComposerScreen.js:535-549`). Sans amorce, les +/- de
 * la répartition partent de `undefined` et produisent des NaN.
 */
function applyHours(draft: ActivationDraft, nextHours: number): ActivationDraft {
    const nextSubjectIds = allowsMoreSubjectsThanHours(draft.pickedClass)
        ? draft.subjectIds
        : draft.subjectIds.slice(0, nextHours)

    return {
        ...draft,
        hoursPerWeek: nextHours,
        subjectIds: nextSubjectIds,
        hoursAllocation:
            nextSubjectIds.length > 0 && nextSubjectIds.length <= nextHours
                ? defaultSubjectHoursAllocation(nextSubjectIds, nextHours)
                : {},
    }
}

/** Port de `onChangeSubjects` (`ActivationComposerScreen.js:583-593`). */
function applySubjects(draft: ActivationDraft, nextSubjectIds: (string | number)[]): ActivationDraft {
    return {
        ...draft,
        subjectIds: nextSubjectIds,
        hoursAllocation:
            nextSubjectIds.length > 0 && nextSubjectIds.length <= draft.hoursPerWeek
                ? defaultSubjectHoursAllocation(nextSubjectIds, draft.hoursPerWeek)
                : {},
    }
}

/**
 * Une heure donnée à une matière est reprise aux autres — port littéral de
 * `getUpdateAllocationHoursFn` (`HourAllocationStep.js:8-92`).
 *
 * Aucune matière ne descend sous une heure : quand il ne reste pas de quoi en donner une à
 * chacune, la matière modifiée est ramenée à son plafond et le reste est réparti à égalité.
 */
function applyAllocation(draft: ActivationDraft, changedSubjectId: string | number, newHours: number): ActivationDraft {
    const key = String(changedSubjectId)
    const allocation = draft.hoursAllocation
    const hoursPerWeek = draft.hoursPerWeek
    const otherSubjects = draft.subjectIds.map(String).filter((id) => id !== key)

    if (otherSubjects.length === 0) {
        return { ...draft, hoursAllocation: { [key]: hoursPerWeek } }
    }

    const remainingHours = hoursPerWeek - newHours

    if (remainingHours < otherSubjects.length) {
        const maxHours = hoursPerWeek - otherSubjects.length
        const adjustedHours = Math.max(1, Math.min(newHours, maxHours))
        const actualRemaining = hoursPerWeek - adjustedHours
        const next: Record<string, number> = { ...allocation, [key]: adjustedHours }
        const baseHours = Math.floor(actualRemaining / otherSubjects.length)
        const extraHours = actualRemaining % otherSubjects.length
        otherSubjects.forEach((id, index) => {
            next[id] = baseHours + (index < extraHours ? 1 : 0)
        })

        return { ...draft, hoursAllocation: next }
    }

    const next: Record<string, number> = { ...allocation, [key]: newHours }
    const currentOtherTotal = otherSubjects.reduce((sum, id) => sum + (allocation[id] || 1), 0)

    if (currentOtherTotal === 0) {
        const baseHours = Math.floor(remainingHours / otherSubjects.length)
        const extraHours = remainingHours % otherSubjects.length
        otherSubjects.forEach((id, index) => {
            next[id] = baseHours + (index < extraHours ? 1 : 0)
        })

        return { ...draft, hoursAllocation: next }
    }

    let distributed = 0
    otherSubjects.forEach((id, index) => {
        if (index === otherSubjects.length - 1) {
            // La dernière matière absorbe le reste : le total reste exact.
            next[id] = remainingHours - distributed
            return
        }
        const proportion = (allocation[id] || 1) / currentOtherTotal
        const proportionalHours = Math.max(1, Math.round(remainingHours * proportion))
        next[id] = proportionalHours
        distributed += proportionalHours
    })

    return { ...draft, hoursAllocation: next }
}

/**
 * Invariant 3 — la sélection garde les seules cases que la grille de la formule affiche. Même
 * référence quand rien ne tombe : un changement de formule sans dimanche coché ne touche pas aux
 * créneaux.
 */
function applyOffer(draft: ActivationDraft, offerType: string): ActivationDraft {
    const kept = keepSelectableGridSlots(draft.slots, { includeSunday: offerType === "integral" })

    return { ...draft, offerType, slots: kept.length === draft.slots.length ? draft.slots : kept }
}

/** Invariant 1 — sans engagement, l'offre n'est commercialisée qu'à un palier. */
function enforceSoldTier(state: ActivationDraftState): ActivationDraftState {
    const sold = state.soldNoCommitmentHours[state.draft.offerType] ?? null
    if (state.draft.withCommitment || sold === null || state.draft.hoursPerWeek === sold) return state

    return { ...state, draft: applyHours(state.draft, sold) }
}

export function activationDraftReducer(
    state: ActivationDraftState,
    action: ActivationDraftAction
): ActivationDraftState {
    switch (action.type) {
        case "SYNC":
            return enforceSoldTier({ draft: action.draft, soldNoCommitmentHours: action.soldNoCommitmentHours })
        case "SET_CLASS": {
            const draft = { ...state.draft, pickedClass: action.value }
            // Invariant 2 — la classe refermée retaille la sélection.
            const trimmed =
                !allowsMoreSubjectsThanHours(action.value) && draft.subjectIds.length > draft.hoursPerWeek
                    ? applySubjects(draft, draft.subjectIds.slice(0, draft.hoursPerWeek))
                    : draft

            return { ...state, draft: trimmed }
        }
        case "SET_HOURS":
            return enforceSoldTier({ ...state, draft: applyHours(state.draft, action.value) })
        case "SET_SUBJECTS":
            return { ...state, draft: applySubjects(state.draft, action.value) }
        case "SET_ALLOCATION":
            return { ...state, draft: applyAllocation(state.draft, action.subjectId, action.hours) }
        case "SET_SLOTS":
            return { ...state, draft: { ...state.draft, slots: action.value } }
        case "SET_RESUME_DATE":
            return { ...state, draft: { ...state.draft, pickedResumeDate: action.value } }
        case "SET_OFFER":
            return enforceSoldTier({ ...state, draft: applyOffer(state.draft, action.value) })
        case "SET_COMMITMENT":
            return enforceSoldTier({ ...state, draft: { ...state.draft, withCommitment: action.value } })
        default:
            return state
    }
}
